/**
 * Alert Service — Predictive alert computation engine.
 *
 * R17 LOW-2 audit fix: Computes 10 alert helpers emitting 12 `type` labels.
 * Helpers: overtime, schedule, quality, activity, budget, batchStuck,
 *          revisionSpiral, escalation, idle, projection (Phase 5.5 Step 6).
 * Types:   overtime, schedule, quality, activity, budget, batch_stuck,
 *          revision_spiral, escalation, idle, projection_overdue,
 *          projection_deadline, projection_budget.
 * All functions are pure — accept data, return alerts. No DB calls. No
 * side effects. Every helper accepts an optional `now` param (default
 * `Date.now()`) so callers can pin a shared wall-clock instant across
 * all helpers for same-job-same-numbers consistency (see R13 MED-2,
 * R14 MED-1, R15 MED-1, R16 LOW-1 audit chain).
 *
 * Each alert: { type, severity, title, message, entityId, entityName, action, dismissKey }
 * Severity: 'critical' | 'warning' | 'info' (positive)
 */

import { DEFAULTS, WORKING_ASSIGNMENT_STATUSES } from '../constants/enums';
import { resolveAssignmentRate } from '../utils/projectMetrics';

// ─── 1. Overtime Prediction ──────────────────────────────

/**
 * Detect annotators on pace to exceed their weekly hour limit.
 *
 * @param {Array} assignments - [{ individual_id, individual: { name }, hours_logged }]
 * @param {Object} insightfulHours - { individualId: { work_hours, days_active } }
 * @param {number} maxHoursPerWeek - from job.hours_per_week_max (default 40)
 * @param {number} daysIntoWeek - how many working days into the current week (1-7)
 * @returns {Array} alerts
 */
export function computeOvertimeAlerts(assignments, insightfulHours, maxHoursPerWeek = 40, daysIntoWeek = null, now = Date.now()) {
  const alerts = [];
  // R15 MED-1 audit fix: accept injected `now` (Date or ms-since-epoch) so
  // this helper's `dayOfWeek` and `dismissKey` bucket match the same wall-
  // clock instant used by the rest of the alert feed. Previously read a
  // fresh `new Date()` — breaking the same-job-same-numbers invariant at
  // midnight boundaries (dismissed alert could reappear under a different
  // YYYY-MM-DD key). Matches the R13 MED-2 + R14 MED-1 + LOW-1 pattern.
  const nowDate = now instanceof Date ? now : new Date(now);
  const dayOfWeek = daysIntoWeek ?? (nowDate.getDay() === 0 ? 7 : nowDate.getDay()); // 1=Mon, 7=Sun
  const dayBucket = nowDate.toISOString().slice(0, 10);

  for (const a of (assignments || [])) {
    const id = a.individual_id || a.individual?.id;
    const name = a.individual?.name || 'Unknown';
    const tracked = insightfulHours?.[id];
    if (!tracked || !tracked.work_hours) continue;

    const weeklyHours = tracked.work_hours;
    const projectedWeekly = dayOfWeek > 0 ? (weeklyHours / dayOfWeek) * 5 : weeklyHours; // Project to 5-day week
    const _excess = projectedWeekly - maxHoursPerWeek;

    if (projectedWeekly > maxHoursPerWeek * 1.3) {
      alerts.push({
        type: 'overtime', severity: 'critical',
        title: `${name} projected ${Math.round(projectedWeekly)}h this week`,
        message: `On pace for ${Math.round(projectedWeekly)} hours (limit: ${maxHoursPerWeek}h). Already logged ${weeklyHours.toFixed(1)}h by day ${dayOfWeek}.`,
        entityId: id, entityName: name,
        action: 'Review workload and consider reassigning batches.',
        dismissKey: `overtime-${id}-${dayBucket}`,
      });
    } else if (projectedWeekly > maxHoursPerWeek * 1.1) {
      alerts.push({
        type: 'overtime', severity: 'warning',
        title: `${name} nearing overtime (${Math.round(projectedWeekly)}h projected)`,
        message: `${weeklyHours.toFixed(1)}h logged by day ${dayOfWeek}. Projected: ${Math.round(projectedWeekly)}h / ${maxHoursPerWeek}h limit.`,
        entityId: id, entityName: name,
        action: 'Monitor hours — may need to slow down.',
        dismissKey: `overtime-${id}-${dayBucket}`,
      });
    }
  }
  return alerts;
}

// ─── 2. Behind / Ahead of Schedule ───────────────────────

/**
 * Predict whether a job will finish on time based on current velocity.
 *
 * @param {Array} batches - annotation_batch rows
 * @param {Object} job - { end_date, title }
 * @param {number} now - timestamp (for testability)
 * @returns {Array} alerts
 */
export function computeScheduleAlerts(batches, job, now = Date.now(), assignments = []) {
  const alerts = [];
  if (!job) return alerts;

  // Primary source: batch-level items_completed (matches dashboard gauge visuals +
  // Don's design principle: prefer batchSummary over stale assignment.annotations_completed).
  // This fix closes Bug 35 — alert was firing "significantly behind" for Foley (87% complete,
  // 14d ahead per dashboard) because some assignments had partial stale annotations_completed
  // values which summed low, while the fallback to batch data was gated on annotationsDone === 0.
  //
  // Phase 11 slot 12 (§8.5 row 5 / PRD-MET-004): the assignment fallback now
  // filters to WORKING_ASSIGNMENT_STATUSES so the count matches what
  // projectJob computes internally (it filters to WORKING). Without the
  // filter, completed-but-not-archived assignments inflated the fallback,
  // and a job with all-overlap batches could disagree with the projection
  // on the same render.
  const annotationsTarget = job.annotations_target || 0;
  const mainBatches = (batches || []).filter(b => !b.is_overlap);
  const batchCompleted = mainBatches.reduce((s, b) => s + (b.items_completed || 0), 0);
  const workingAssignments = assignments.filter(a => WORKING_ASSIGNMENT_STATUSES.includes(a.status));
  const assignmentCompleted = workingAssignments.reduce((s, a) => s + (a.annotations_completed || 0), 0);

  const totalItems = annotationsTarget > 0 ? annotationsTarget : mainBatches.reduce((s, b) => s + (b.total_items || 0), 0);
  // Prefer batches; only fall back to assignment aggregate if no batch data exists at all
  const completedItems = batchCompleted > 0 ? batchCompleted : assignmentCompleted;
  const remainingItems = totalItems - completedItems;

  if (totalItems === 0 || remainingItems <= 0) return alerts;

  // Compute velocity from project start date (not batch start)
  const projectStart = job.start_date ? new Date(job.start_date).getTime() : null;
  const startedBatches = mainBatches.filter(b => b.started_at);
  const earliestStart = projectStart || (startedBatches.length > 0 ? Math.min(...startedBatches.map(b => new Date(b.started_at).getTime())) : null);
  if (!earliestStart) return alerts;

  const daysElapsed = (now - earliestStart) / 86400000;
  if (daysElapsed < 0.5) return alerts; // Not enough data

  const velocity = completedItems / daysElapsed;
  const projectedDaysLeft = velocity > 0 ? remainingItems / velocity : Infinity;

  if (!job.end_date && !job.sla_delivery_date) {
    alerts.push({
      type: 'schedule', severity: 'info',
      title: `${job.title}: no end date set`,
      message: `Schedule tracking unavailable — set a deadline to enable pace monitoring.`,
      entityId: job.id, entityName: job.title,
      action: 'Set an end date in project settings.',
      dismissKey: `schedule-nodate-${job.id}`,
    });
    return alerts;
  }

  const endDate = job.end_date || job.sla_delivery_date;
  if (endDate) {
    const deadline = new Date(endDate).getTime();
    const daysToDeadline = (deadline - now) / 86400000;
    const needed = daysToDeadline > 0 ? remainingItems / daysToDeadline : Infinity;

    if (velocity < needed * 0.5) {
      alerts.push({
        type: 'schedule', severity: 'critical',
        title: `${job.title}: significantly behind schedule`,
        message: `Current pace: ${Math.round(velocity)} items/day. Needed: ${Math.round(needed)} items/day. ${remainingItems.toLocaleString()} items remaining with ${Math.round(daysToDeadline)} days left.`,
        entityId: job.id, entityName: job.title,
        action: 'Add team members or extend deadline.',
        dismissKey: `schedule-behind-${job.id}`,
      });
    } else if (velocity < needed * 0.95) {
      // Alert at 95% of needed pace — aligns with pace gauge showing "Behind" at 5% gap
      alerts.push({
        type: 'schedule', severity: velocity < needed * 0.8 ? 'warning' : 'info',
        title: `${job.title}: falling behind`,
        message: `Pace: ${Math.round(velocity)}/day vs needed ${Math.round(needed)}/day. May miss deadline by ~${Math.ceil(projectedDaysLeft - daysToDeadline)} days.`,
        entityId: job.id, entityName: job.title,
        action: 'Monitor closely — consider adding capacity.',
        dismissKey: `schedule-behind-${job.id}`,
      });
    } else if (velocity > needed * 1.3 && daysToDeadline > 2) {
      alerts.push({
        type: 'schedule', severity: 'info',
        title: `${job.title}: ahead of schedule`,
        message: `${Math.ceil(daysToDeadline - projectedDaysLeft)} days ahead at current pace. Projected finish: ${new Date(now + projectedDaysLeft * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`,
        entityId: job.id, entityName: job.title,
        action: 'On track — great progress.',
        dismissKey: `schedule-ahead-${job.id}`,
      });
    }
  }
  return alerts;
}

// ─── 3. Quality Dropping ─────────────────────────────────

/**
 * Detect quality trends declining over 3+ consecutive entries.
 *
 * @param {Array} qualityLogs - sorted by created_at DESC
 * @param {Object} job - { title, id }
 * @param {number} threshold - minimum acceptable quality (default 90)
 * @returns {Array} alerts
 */
export function computeQualityAlerts(qualityLogs, job, threshold = DEFAULTS.QUALITY_ALERT_THRESHOLD || 90) {
  const alerts = [];
  if (!qualityLogs || qualityLogs.length < 3 || !job) return alerts;

  // Group by annotator, sort each group by created_at DESC (newest first)
  const byAnnotator = {};
  const sortedLogs = [...qualityLogs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  for (const log of sortedLogs) {
    if (log.accuracy_percent == null) continue;
    const id = log.individual_id;
    if (!byAnnotator[id]) byAnnotator[id] = [];
    byAnnotator[id].push(Number(log.accuracy_percent));
  }

  for (const [annotatorId, scores] of Object.entries(byAnnotator)) {
    const latestName = sortedLogs.find(l => l.individual_id === annotatorId)?.individual?.name || 'Annotator';
    const latestScore = scores[0]; // most recent (already sorted DESC)

    // Alert 1: Individual below quality threshold (any single review)
    if (latestScore < threshold) {
      alerts.push({
        type: 'quality', severity: latestScore < threshold - 10 ? 'critical' : 'warning',
        title: `${latestName}: below quality threshold (${latestScore.toFixed(0)}%)`,
        message: `Latest accuracy ${latestScore.toFixed(1)}% on ${job.title} — below ${threshold}% threshold.`,
        entityId: annotatorId, entityName: latestName,
        action: 'Review recent work and provide feedback.',
        dismissKey: `quality-low-${annotatorId}-${job.id}`,
      });
    }

    // Alert 2: Declining trend (3+ consecutive drops)
    if (scores.length >= 3) {
      const recent = scores.slice(0, 3);
      if (recent[0] < recent[1] && recent[1] < recent[2]) {
        const drop = recent[2] - recent[0];
        const severity = recent[0] < threshold ? 'critical' : drop > 5 ? 'warning' : 'info';
        alerts.push({
          type: 'quality', severity,
          title: `${latestName}: quality declining (${recent[2].toFixed(0)}% → ${recent[0].toFixed(0)}%)`,
          message: `Accuracy dropped ${drop.toFixed(1)}% over last 3 reviews on ${job.title}. ${recent[0] < threshold ? `Now below ${threshold}% threshold.` : ''}`,
          entityId: annotatorId, entityName: latestName,
          action: recent[0] < threshold ? 'Review work and consider reassignment.' : 'Schedule a calibration session.',
          dismissKey: `quality-declining-${annotatorId}-${job.id}`,
        });
      }
    }
  }
  return alerts;
}

// ─── 4. Annotator Going Quiet ────────────────────────────

/**
 * Detect annotators who stopped logging hours unexpectedly.
 *
 * @param {Array} assignments - active assignments with individual info
 * @param {Object} insightfulHours - { individualId: { work_hours, days_active } }
 * @param {number} expectedDaysPerWeek - how many days they usually work (default 4)
 * @returns {Array} alerts
 */
export function computeActivityAlerts(assignments, insightfulHours, expectedDaysPerWeek = 4) {
  const alerts = [];

  // Only fire time-tracking alerts if integration is configured (has any data)
  const hasIntegrationData = insightfulHours && Object.keys(insightfulHours).length > 0;

  for (const a of (assignments || [])) {
    const id = a.individual_id || a.individual?.id;
    const name = a.individual?.name || 'Unknown';
    const tracked = insightfulHours?.[id];

    // No tracking data — only alert if integration exists for other annotators
    if (!tracked && a.status === 'Active' && hasIntegrationData) {
      alerts.push({
        type: 'activity', severity: 'warning',
        title: `${name}: no time tracking data`,
        message: `Active on project but no Insightful hours recorded. May not be tracking.`,
        entityId: id, entityName: name,
        action: 'Verify time tracking is set up correctly.',
        dismissKey: `activity-nodata-${id}`,
      });
      continue;
    }

    if (!tracked) continue;

    // Very few active days → might be going quiet
    if (tracked.days_active <= 1 && tracked.work_hours < 2 && a.status === 'Active') {
      alerts.push({
        type: 'activity', severity: 'warning',
        title: `${name}: minimal activity this period`,
        message: `Only ${tracked.days_active} active day${tracked.days_active !== 1 ? 's' : ''} and ${tracked.work_hours.toFixed(1)}h logged. Usually active ${expectedDaysPerWeek} days/week.`,
        entityId: id, entityName: name,
        action: 'Check in — they may be blocked or unavailable.',
        dismissKey: `activity-quiet-${id}`,
      });
    }
  }
  return alerts;
}

// ─── 5. Budget Burn Rate ─────────────────────────────────

/**
 * Predict if current spend rate will exceed budget before deadline.
 *
 * @param {Array} assignments - with hours_logged and rate
 * @param {Object} job - { budget, start_date, end_date, title, id }
 * @param {number} now - timestamp
 * @returns {Array} alerts
 */
export function computeBudgetAlerts(assignments, job, now = Date.now()) {
  const alerts = [];
  if (!job?.budget || !assignments?.length) return alerts;

  const budget = parseFloat(job.budget);
  if (!budget || budget <= 0) return alerts;

  // R8 MEDIUM-1 audit fix: route through canonical resolveAssignmentRate
  // so computeBudgetAlerts' totalSpent agrees with proj.dollarsSpent in
  // the SAME alert feed. Previously `a.rate || 0` returned 0 for
  // `rate=null, confirmed_rate=N` assignments while projection's
  // dollarsSpent correctly resolved to `N×hours` — two alert types on
  // the same project reported divergent budget state.
  const totalSpent = assignments.reduce((s, a) => s + ((a.hours_logged || 0) * resolveAssignmentRate(a)), 0);
  const utilization = (totalSpent / budget) * 100;

  const startDate = job.start_date ? new Date(job.start_date).getTime() : null;
  const endDate = job.end_date ? new Date(job.end_date).getTime() : null;

  if (startDate && endDate && endDate > now) {
    const daysElapsed = (now - startDate) / 86400000;
    const totalDays = (endDate - startDate) / 86400000;
    const dailyBurn = daysElapsed > 0 ? totalSpent / daysElapsed : 0;
    const projectedTotal = dailyBurn * totalDays;
    const daysLeft = (endDate - now) / 86400000;
    const projectedExceedDate = dailyBurn > 0 ? new Date(startDate + (budget / dailyBurn) * 86400000) : null;

    if (projectedTotal > budget * 1.2) {
      alerts.push({
        type: 'budget', severity: 'critical',
        title: `${job.title}: budget will be exceeded`,
        message: `At $${Math.round(dailyBurn)}/day, projected total: $${Math.round(projectedTotal).toLocaleString()} (budget: $${Math.round(budget).toLocaleString()}). ${projectedExceedDate && projectedExceedDate.getTime() < endDate ? `Exceeds budget by ${projectedExceedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.` : ''}`,
        entityId: job.id, entityName: job.title,
        action: 'Reduce team hours or increase budget.',
        dismissKey: `budget-exceed-${job.id}`,
      });
    } else if (utilization > 80) {
      alerts.push({
        type: 'budget', severity: 'warning',
        title: `${job.title}: ${Math.round(utilization)}% of budget used`,
        message: `$${Math.round(totalSpent).toLocaleString()} spent of $${Math.round(budget).toLocaleString()} budget. ${Math.round(daysLeft)} days remaining.`,
        entityId: job.id, entityName: job.title,
        action: 'Monitor spend rate closely.',
        dismissKey: `budget-high-${job.id}`,
      });
    }
  } else if (utilization > 90) {
    alerts.push({
      type: 'budget', severity: 'critical',
      title: `${job.title}: ${Math.round(utilization)}% of budget used`,
      message: `$${Math.round(totalSpent).toLocaleString()} of $${Math.round(budget).toLocaleString()}. Near limit.`,
      entityId: job.id, entityName: job.title,
      action: 'Review remaining scope.',
      dismissKey: `budget-limit-${job.id}`,
    });
  }
  return alerts;
}

// ─── 5b. Cost-vs-Progress Efficiency Gap ─────────────────

/**
 * Detect cost-vs-progress efficiency gap: spending NOW has outpaced delivery
 * NOW by ≥20 percentage points. Distinct from computeBudgetAlerts which is
 * utilization-based + projection-based; this is purely "right now" efficiency.
 *
 * Why it's a separate alert (P22.2.1, Chapter 3 dogfood close 2026-04-30):
 *   The chapter-3-plan §22.3 BEHIND scenario "cost > 60% budget but progress
 *   < 40% deliverables" is a real customer signal: cost is racing ahead of
 *   value delivered. Existing computeBudgetAlerts only flags utilization
 *   (totalSpent / budget) or projected exceed (dailyBurn × totalDays). Both
 *   miss the case where the project is on track for budget but is producing
 *   noticeably less per dollar than expected.
 *
 * Math:
 *   spendPct    = totalSpent / budget × 100
 *   progressPct = totalCompleted / totalTarget × 100
 *   gap         = spendPct - progressPct
 *   gap > 20pp  → warning (cost outpacing delivery; investigate)
 *   gap > 40pp  → critical (significant efficiency loss; intervene now)
 *
 * Pre-conditions (no alert if missing — avoids false positives on fresh
 * projects):
 *   - job.budget set + > 0
 *   - job.annotations_target set + > 0 (need a denominator for progressPct)
 *   - any spend has happened (totalSpent > 0)
 *
 * @param {Array} assignments - with hours_logged + rate (uses resolveAssignmentRate)
 * @param {Object} job - { id, title, budget, annotations_target }
 * @param {Array} batches - all batches for the job (for items_completed sum)
 * @returns {Array} alerts of type 'cost_overrun'
 */
export function computeCostOverrunAlerts(assignments, job, batches) {
  const alerts = [];
  if (!job?.budget || !job?.annotations_target) return alerts;
  if (!assignments?.length) return alerts;

  const budget = parseFloat(job.budget);
  const target = parseInt(job.annotations_target, 10);
  if (!budget || budget <= 0 || !target || target <= 0) return alerts;

  // Sum spend (mirrors computeBudgetAlerts via resolveAssignmentRate so the
  // two alerts agree on dollarsSpent for the same project).
  const totalSpent = assignments.reduce(
    (s, a) => s + ((a.hours_logged || 0) * resolveAssignmentRate(a)),
    0
  );
  if (totalSpent <= 0) return alerts;

  // Sum delivered items. Excludes overlap batches (which are QA samples,
  // not customer-billable progress).
  const totalCompleted = (batches || [])
    .filter(b => !b.is_overlap)
    .reduce((s, b) => s + (b.items_completed || 0), 0);

  const spendPct = (totalSpent / budget) * 100;
  const progressPct = (totalCompleted / target) * 100;
  const gap = spendPct - progressPct;

  // No alert when within tolerance — most projects have small drift.
  if (gap <= 20) return alerts;

  const severity = gap > 40 ? 'critical' : 'warning';
  alerts.push({
    type: 'cost_overrun',
    severity,
    title: `${job.title}: cost ${Math.round(gap)}pp ahead of progress`,
    message: `$${Math.round(totalSpent).toLocaleString()} spent (${Math.round(spendPct)}% of budget) but only ${totalCompleted.toLocaleString()} of ${target.toLocaleString()} items delivered (${Math.round(progressPct)}%). Efficiency gap of ${Math.round(gap)} percentage points — cost is outpacing delivery.`,
    entityId: job.id,
    entityName: job.title,
    action: severity === 'critical'
      ? 'Investigate immediately — likely needs scope cut, calibration, or team change.'
      : 'Review hours-per-item and quality metrics; identify the source of the gap.',
    dismissKey: `cost-overrun-${job.id}`,
  });

  return alerts;
}

// ─── 6. Batch Stuck ──────────────────────────────────────

/**
 * Detect batches stuck "In Progress" longer than average.
 *
 * @param {Array} batches - all batches for a job
 * @param {number} now - timestamp
 * @returns {Array} alerts
 */
export function computeBatchStuckAlerts(batches, now = Date.now()) {
  const alerts = [];
  if (!batches?.length) return alerts;

  // Compute average completion time from finished batches
  const completed = batches.filter(b => b.started_at && b.submitted_at && !b.is_overlap);
  if (completed.length === 0) return alerts; // No baseline

  const avgHours = completed.reduce((s, b) => {
    return s + (new Date(b.submitted_at).getTime() - new Date(b.started_at).getTime()) / 3600000;
  }, 0) / completed.length;

  // Check in-progress batches
  const inProgress = batches.filter(b => b.status === 'In Progress' && b.started_at);
  for (const b of inProgress) {
    const hoursElapsed = (now - new Date(b.started_at).getTime()) / 3600000;
    const ratio = avgHours > 0 ? hoursElapsed / avgHours : 0;

    if (ratio > DEFAULTS.BATCH_STUCK_CRITICAL) {
      alerts.push({
        type: 'batch_stuck', severity: 'critical',
        title: `Batch #${b.batch_number} stuck (${Math.round(hoursElapsed / 24)}d in progress)`,
        message: `In progress for ${Math.round(hoursElapsed)}h — average is ${Math.round(avgHours)}h (${ratio.toFixed(1)}× slower). Assigned to ${b.individual?.name || 'unknown'}.`,
        entityId: b.id, entityName: `Batch #${b.batch_number}`,
        action: 'Check with annotator — may be blocked.',
        dismissKey: `stuck-${b.id}`,
      });
    } else if (ratio > DEFAULTS.BATCH_STUCK_WARNING) {
      alerts.push({
        type: 'batch_stuck', severity: 'warning',
        title: `Batch #${b.batch_number} slower than average`,
        message: `${Math.round(hoursElapsed)}h in progress (avg: ${Math.round(avgHours)}h). ${b.individual?.name || 'Assigned annotator'} may need support.`,
        entityId: b.id, entityName: `Batch #${b.batch_number}`,
        action: 'Monitor progress.',
        dismissKey: `stuck-${b.id}`,
      });
    }
  }
  return alerts;
}

// ─── 7. Revision Spiral ──────────────────────────────────

/**
 * Detect batches revised too many times.
 *
 * @param {Array} batches
 * @param {number} maxRevisions - from org_qa_config (default 3)
 * @returns {Array} alerts
 */
export function computeRevisionSpiralAlerts(batches, maxRevisions = 3) {
  const alerts = [];
  for (const b of (batches || [])) {
    if ((b.revision_count || 0) >= maxRevisions + 2) {
      alerts.push({
        type: 'revision_spiral', severity: 'critical',
        title: `Batch #${b.batch_number}: ${b.revision_count} revisions`,
        message: `Revised ${b.revision_count} times (max: ${maxRevisions}). Assigned to ${b.individual?.name || 'unknown'}. Quality issue persists.`,
        entityId: b.id, entityName: `Batch #${b.batch_number}`,
        action: 'Reassign to a senior annotator.',
        dismissKey: `spiral-${b.id}`,
      });
    } else if ((b.revision_count || 0) >= maxRevisions) {
      alerts.push({
        type: 'revision_spiral', severity: 'warning',
        title: `Batch #${b.batch_number}: ${b.revision_count} revisions`,
        message: `Hit revision limit (${maxRevisions}). Consider reassignment or calibration.`,
        entityId: b.id, entityName: `Batch #${b.batch_number}`,
        action: 'Review annotator performance on this batch.',
        dismissKey: `spiral-${b.id}`,
      });
    }
  }
  return alerts;
}

// ─── 8. Client Escalation Detection ─────────────────────

/**
 * Detect client disengagement signals:
 * - Delivered batches not reviewed within 3+ days
 * - High rejection rate spike (>30% in recent batches vs historical)
 */
export function computeEscalationAlerts(batches = [], now = Date.now()) {
  const alerts = [];
  const DAY_MS = 86400000;

  // 8a. Batches delivered but not reviewed (no accepted_at, no rejected status)
  const deliveredUnreviewed = batches.filter(b =>
    b.status === 'Delivered' && b.delivered_at && !b.accepted_at
  );

  for (const batch of deliveredUnreviewed) {
    const deliveredTime = new Date(batch.delivered_at).getTime();
    const daysSince = (now - deliveredTime) / DAY_MS;

    if (daysSince >= DEFAULTS.UNREVIEWED_CRITICAL_DAYS) {
      alerts.push({
        type: 'escalation',
        severity: 'critical',
        title: `Batch #${batch.batch_number} unreviewed for ${Math.round(daysSince)} days`,
        message: `Delivered ${Math.round(daysSince)} days ago with no client review. Client may be disengaged.`,
        entityId: batch.id,
        entityName: `Batch #${batch.batch_number}`,
        action: 'Follow up with client on pending review',
        dismissKey: `escalation-unreviewed-${batch.id}`,
      });
    } else if (daysSince >= DEFAULTS.UNREVIEWED_WARNING_DAYS) {
      alerts.push({
        type: 'escalation',
        severity: 'warning',
        title: `Batch #${batch.batch_number} pending review (${Math.round(daysSince)}d)`,
        message: `Delivered ${Math.round(daysSince)} days ago. Client has not started review.`,
        entityId: batch.id,
        entityName: `Batch #${batch.batch_number}`,
        action: 'Consider sending a follow-up to the client',
        dismissKey: `escalation-pending-${batch.id}`,
      });
    }
  }

  // 8b. Rejection rate spike — compare recent vs historical
  // In the 4-state lifecycle, "reviewed" = batch has post-review data. A batch
  // is reviewed when it's Accepted, Delivered (with rejected items), or bounced
  // back to In Progress with a rejection_note.
  const reviewedBatches = batches.filter(b => {
    if (b.items_rejected == null) return false;
    if (b.status === 'Accepted' || b.status === 'Delivered') return true;
    if (b.status === 'In Progress' && b.rejection_note) return true;
    return false;
  });

  if (reviewedBatches.length >= 4) {
    const recent = reviewedBatches.slice(0, 3); // Last 3 batches
    const historical = reviewedBatches.slice(3); // Everything before

    const recentRejectionRate = recent.reduce((s, b) => s + (b.items_rejected || 0), 0) /
      Math.max(1, recent.reduce((s, b) => s + (b.total_items || 1), 0));

    const historicalRejectionRate = historical.length > 0
      ? historical.reduce((s, b) => s + (b.items_rejected || 0), 0) /
        Math.max(1, historical.reduce((s, b) => s + (b.total_items || 1), 0))
      : 0;

    if (recentRejectionRate > DEFAULTS.REJECTION_SPIKE_THRESHOLD && recentRejectionRate > historicalRejectionRate * 2) {
      alerts.push({
        type: 'escalation',
        severity: 'critical',
        title: `Client rejection rate spiked to ${Math.round(recentRejectionRate * 100)}%`,
        message: `Recent batches: ${Math.round(recentRejectionRate * 100)}% rejection rate vs historical ${Math.round(historicalRejectionRate * 100)}%. Client satisfaction may be declining.`,
        entityId: null,
        entityName: 'Client Satisfaction',
        action: 'Schedule a quality calibration session with the client',
        dismissKey: `escalation-rejection-spike-${Math.round(recentRejectionRate * 100)}`,
      });
    }
  }

  return alerts;
}

// ─── Orchestrator ────────────────────────────────────────

// ─── 9. Idle Annotator Detection ────────────────────────

/**
 * Detect annotators who haven't been active recently.
 * Uses individual.last_active_at to determine idle status.
 */
export function computeIdleAlerts(assignments, annotators, thresholdDays = DEFAULTS.IDLE_THRESHOLD_DAYS || 3, now = Date.now()) {
  // R15 MED-1 audit fix: accept injected `now` so `daysSince` and
  // `dismissKey` bucket match the rest of the alert feed. Previously read
  // its own fresh `Date.now()` → at midnight, dismissed idle alert could
  // reappear under a different YYYY-MM-DD key. Same fix pattern as R14
  // LOW-1 (projection-overdue dismissKey) + R15 MED-1 (overtime).
  const alerts = [];
  const nowMs = now instanceof Date ? now.getTime() : now;
  const dayBucket = new Date(nowMs).toISOString().slice(0, 10);
  for (const a of (assignments || [])) {
    if (a.status !== 'Active') continue;
    const annotator = (annotators || []).find(ann => ann.id === (a.individual_id || a.individual?.id));
    const lastActive = annotator?.last_active_at ? new Date(annotator.last_active_at).getTime() : null;
    if (!lastActive) continue;
    const daysSince = (nowMs - lastActive) / 86400000;
    if (daysSince >= thresholdDays) {
      const name = annotator?.name || a.individual?.name || 'Annotator';
      alerts.push({
        type: 'idle',
        severity: daysSince >= thresholdDays * 2 ? 'critical' : 'warning',
        title: `${name}: idle for ${Math.round(daysSince)} days`,
        message: `Last active ${Math.round(daysSince)} days ago on ${a.job?.title || 'project'}. May be blocked or unavailable.`,
        entityId: a.individual_id || a.individual?.id,
        entityName: name,
        action: 'Check in with the annotator.',
        dismissKey: `idle-${a.individual_id || a.individual?.id}-${dayBucket}`,
      });
    }
  }
  return alerts;
}

/**
 * Compute all alerts from available data.
 * Returns sorted by severity (critical first, then warning, then info).
 *
 * @param {Object} data
 * @param {Array} data.assignments - active assignments
 * @param {Object} data.insightfulHours - tracked hours per individual
 * @param {Array} data.batches - all batches for the job(s)
 * @param {Array} data.qualityLogs - quality log entries
 * @param {Object} data.job - job record (for single-job context)
 * @param {number} [data.maxHoursPerWeek] - from job config
 * @param {number} [data.maxRevisions] - from org_qa_config
 * @returns {Array} sorted alerts
 */
/**
 * Phase 5.5 Step 6 — Probability-tier alerts from Phase 5 projections.
 *
 * Emits alerts when the forecast-based probability of hitting the
 * deadline (or budget) falls into at-risk tiers. Distinct from
 * `computeScheduleAlerts` (which is pace-based — timeline % vs
 * completion %). The two are complementary:
 *   - Schedule alert = "you're BEHIND on timeline right now"
 *   - Projection alert = "forecast says you will MISS regardless"
 *
 * Tier → severity mapping:
 *   - very_low deadline tier → critical ("will likely miss")
 *   - low deadline tier → warning ("at risk")
 *   - fifty_fifty + deadline within 7 days → warning ("coin-flip on imminent deadline")
 *   - overdue (explicit flag) → critical (different action copy)
 *   - very_low cost tier → critical ("will exceed budget")
 *   - low cost tier → warning
 *
 * Hysteresis (R4-audit-aware): to avoid flapping at tier boundaries, an
 * alert is only emitted if the current tier has PERSISTED across the
 * last `minSnapshots` snapshots. Snapshots are held in-memory in the
 * caller's state — specifically `useAdminData.js`'s `projectionHistoriesRef`
 * useRef, which is lost on page reload. Per-project callers (ProjectDashboard,
 * Admin `/god` route) pass `minSnapshotsForHysteresis: 1` to disable
 * hysteresis entirely, so only the admin-wide alert feed actually uses
 * this. A durable `projection_history` table is deferred to Phase 6.
 *
 * R24 LOW-1 audit fix: removed false "sessionStorage-backed in UI callers"
 * claim that was aspirational from the original Phase 5.5 Step 6 commit
 * (a2947cc) but never matched the shipped implementation.
 *
 * @param {object} proj — projectJob bundle (contains deadlineHit, costOverrun)
 * @param {object} job — job row (for entity attribution)
 * @param {{
 *   deadline?: Array<{tier: string, computedAt: number}>,
 *   cost?: Array<{tier: string, computedAt: number}>
 * }} [snapshotHistory={}] — recent tier snapshots for hysteresis, one
 *   history per tier family (deadline vs cost are independent signals
 *   and must not cross-confirm each other). Latest snapshot first.
 * @param {number} [minSnapshots=2] — tier must persist across this many
 *   consecutive snapshots before firing. 2 = needs one prior confirming.
 *   Setting to 1 disables hysteresis (alerts fire instantly).
 * @returns {Array} alerts
 */
export function computeProjectionAlerts(proj, job, snapshotHistory = {}, minSnapshots = 2, now = Date.now()) {
  const alerts = [];
  if (!proj || !job) return alerts;
  const entityId = job.id;
  const entityName = job.title || 'Project';

  const deadlineHist = Array.isArray(snapshotHistory?.deadline) ? snapshotHistory.deadline : [];
  const costHist = Array.isArray(snapshotHistory?.cost) ? snapshotHistory.cost : [];

  // Helper — checks that `tier` has PERSISTED across the last
  // (minSnapshots - 1) snapshots in the given history.
  // Always-true at minSnapshots ≤ 1 (disables hysteresis).
  const hasPersisted = (tier, history) => {
    if (minSnapshots <= 1) return true;
    const recent = history.slice(0, minSnapshots - 1);
    if (recent.length < minSnapshots - 1) return false;  // not enough history yet
    return recent.every((s) => s?.tier === tier);
  };

  const dh = proj.deadlineHit;
  const co = proj.costOverrun;

  // ─── Deadline-hit alerts ───
  if (dh) {
    // Overdue (explicit flag) — always critical, action is urgent.
    if (dh.overdue) {
      alerts.push({
        type: 'projection_overdue', severity: 'critical',
        title: `${entityName} is overdue`,
        message: `Deadline passed ${Math.abs(dh.daysDelta || 0)} day${Math.abs(dh.daysDelta || 0) !== 1 ? 's' : ''} ago; work still remaining.`,
        entityId, entityName,
        action: 'Surface to client + add annotators or cut scope.',
        // R14 LOW-1 audit fix: bucket the dismissKey to the injected `now`
        // (not a fresh `new Date()`). At a midnight boundary the two clocks
        // could bucket to different days — a dismissed alert would reappear
        // under a different key a minute later.
        dismissKey: `proj-overdue-${entityId}-${new Date(now).toISOString().slice(0, 10)}`,
      });
    } else if (dh.tier === 'very_low' && hasPersisted('very_low', deadlineHist)) {
      alerts.push({
        type: 'projection_deadline', severity: 'critical',
        title: `${entityName} very likely to miss deadline`,
        message: `Forecast: ${dh.projectedDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '—'} (${Math.abs(dh.daysDelta || 0)} day${Math.abs(dh.daysDelta || 0) !== 1 ? 's' : ''} late). Intervene now.`,
        entityId, entityName,
        action: 'Add annotators or cut scope today.',
        dismissKey: `proj-deadline-${entityId}-${dh.tier}`,
      });
    } else if (dh.tier === 'low' && hasPersisted('low', deadlineHist)) {
      alerts.push({
        type: 'projection_deadline', severity: 'warning',
        title: `${entityName} at risk — review staffing`,
        message: `Forecast: ${dh.projectedDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '—'} (${Math.abs(dh.daysDelta || 0)} day${Math.abs(dh.daysDelta || 0) !== 1 ? 's' : ''} ${(dh.daysDelta || 0) < 0 ? 'late' : 'early'}).`,
        entityId, entityName,
        action: 'Check in with team and review capacity.',
        dismissKey: `proj-deadline-${entityId}-${dh.tier}`,
      });
    } else if (dh.tier === 'fifty_fifty' && dh.deadlineDate) {
      // Coin-flip is only an alert-worthy situation when the deadline is IMMINENT.
      // R13 MED-2 audit fix: use injected `now` (from computeAllAlerts, in turn
      // from useAdminData's sharedNow useMemo) instead of Date.now(). Previously
      // this one line read a fresh wall-clock instant that could disagree with
      // the `dh.daysDelta` computed by projectJob at a slightly earlier moment,
      // producing 1-day-off `daysUntilDeadline` at date boundaries.
      const daysUntilDeadline = Math.ceil((dh.deadlineDate.getTime() - now) / 86400000);
      if (daysUntilDeadline <= 7 && daysUntilDeadline >= 0 && hasPersisted('fifty_fifty', deadlineHist)) {
        alerts.push({
          type: 'projection_deadline', severity: 'warning',
          title: `${entityName} coin-flip on imminent deadline`,
          message: `Deadline in ${daysUntilDeadline} day${daysUntilDeadline !== 1 ? 's' : ''}; forecast hit-probability is 40-60%.`,
          entityId, entityName,
          action: 'Watch daily — a small slip pushes this into "at risk".',
          dismissKey: `proj-deadline-${entityId}-${dh.tier}-${daysUntilDeadline}`,
        });
      }
    }
  }

  // ─── Cost-overrun alerts ───
  if (co) {
    if (co.tier === 'very_low' && hasPersisted('very_low', costHist)) {
      alerts.push({
        type: 'projection_budget', severity: 'critical',
        title: `${entityName} very likely to exceed budget`,
        message: `Forecast total: $${Math.round(co.projectedCost || 0).toLocaleString()}; budget $${Math.round(co.budget || 0).toLocaleString()}.`,
        entityId, entityName,
        action: 'Request budget increase or cut scope.',
        dismissKey: `proj-budget-${entityId}-${co.tier}`,
      });
    } else if (co.tier === 'low' && hasPersisted('low', costHist)) {
      alerts.push({
        type: 'projection_budget', severity: 'warning',
        title: `${entityName} budget at risk`,
        message: `Forecast total: $${Math.round(co.projectedCost || 0).toLocaleString()}; budget $${Math.round(co.budget || 0).toLocaleString()} (${(co.dollarsDelta ?? 0) >= 0 ? 'under' : 'over'}).`,
        entityId, entityName,
        action: 'Monitor cost burn this week.',
        dismissKey: `proj-budget-${entityId}-${co.tier}`,
      });
    }
  }

  return alerts;
}

export function computeAllAlerts(data) {
  const {
    assignments = [], insightfulHours = {}, batches = [],
    qualityLogs = [], annotators = [], job = null,
    maxHoursPerWeek = 40, maxRevisions = 3,
    // Phase 5.5 Step 6 — optional Phase 5 bundle + tier-snapshot history.
    // When provided, projection-tier alerts join the feed. When absent,
    // only the legacy alert types fire (backward compatible).
    projection = null,
    // R14 LOW-6 audit fix: default matches expected shape `{deadline: [],
    // cost: []}`. `computeProjectionAlerts`'s `snapshotHistory` guards
    // (`Array.isArray(snapshotHistory?.deadline) ? ... : []`) defensively
    // handle either shape, but `[]` as a default misled readers. Runtime
    // unchanged. R19 LOW audit fix: function-name refs instead of line numbers.
    projectionSnapshotHistory = {},
    minSnapshotsForHysteresis = 2,
    // R13 MED-2 audit fix: accept injected `now` so the caller (e.g.
    // useAdminData's sharedNow useMemo) can pin the wall-clock instant
    // used by the projection and the alerts to the same value. Back-
    // compatible: defaults to Date.now() when caller doesn't pass.
    // Accepts either a Date or a number (ms-since-epoch).
    now: nowInput,
  } = data;

  // Don't compute alerts for completed/cancelled/paused projects
  // Phase 23.7 (adversarial use case discovery 2026-05-01): Paused added.
  // A paused project shouldn't fire overtime/budget/quality/etc. alerts —
  // the customer has explicitly halted work, and continuing to alert
  // would be noise. Resume puts the project back into the alert flow.
  if (job?.status === 'Completed' || job?.status === 'Cancelled' || job?.status === 'Archived' || job?.status === 'Paused') {
    return [];
  }

  const now = nowInput instanceof Date
    ? nowInput.getTime()
    : (Number.isFinite(nowInput) ? nowInput : Date.now());

  const all = [
    // R15 MED-1 audit fix: thread `now` into overtime + idle helpers too,
    // completing the R13 MED-2 + R14 MED-1 + LOW-1 chain. Previously these
    // two read their own fresh `Date.now()`/`new Date()`, breaking same-
    // job-same-numbers at midnight boundaries (dismiss-bucket drift).
    ...computeOvertimeAlerts(assignments, insightfulHours, maxHoursPerWeek, null, now),
    ...computeScheduleAlerts(batches, job, now, assignments),
    ...computeQualityAlerts(qualityLogs, job),
    ...computeActivityAlerts(assignments, insightfulHours),
    ...computeBudgetAlerts(assignments, job, now),
    // P22.2.1 (Chapter 3 dogfood close 2026-04-30): cost-vs-progress
    // efficiency gap. Distinct from `budget` (utilization-based). Fires
    // when spend outpaces delivery by >20pp. Pure "right now" signal —
    // doesn't require batches in any specific state, just a non-zero
    // spend and a project-level target.
    ...computeCostOverrunAlerts(assignments, job, batches),
    ...computeBatchStuckAlerts(batches, now),
    ...computeRevisionSpiralAlerts(batches, maxRevisions),
    ...computeEscalationAlerts(batches, now),
    ...computeIdleAlerts(assignments, annotators, undefined, now),
    // R13 MED-2 audit fix: thread the shared `now` into projection alerts
    // so `computeProjectionAlerts`'s `daysUntilDeadline` (inside the
    // `dh.tier === 'fifty_fifty' && dh.deadlineDate` branch) reads the same
    // wall-clock instant as the projection it consumes.
    // R19 LOW audit fix: function-name refs instead of line numbers.
    ...computeProjectionAlerts(projection, job, projectionSnapshotHistory, minSnapshotsForHysteresis, now),
  ];

  // Sort: critical first, then warning, then info
  const severityOrder = { critical: 0, warning: 1, info: 2 };
  const sorted = all.sort((a, b) => (severityOrder[a.severity] ?? 99) - (severityOrder[b.severity] ?? 99));
  const deduped = [];
  const seen = new Set();

  for (const alert of sorted) {
    const dedupeKey = alert.dismissKey || `${alert.type}-${alert.entityId || alert.entityName || alert.title}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    deduped.push(alert);
  }

  return deduped;
}

/**
 * Filter out dismissed alerts using localStorage.
 */
export function filterDismissedAlerts(alerts) {
  return alerts.filter(a => {
    try { return !localStorage.getItem(`alert_dismissed_${a.dismissKey}`); }
    catch { return true; } // intentionally empty
  });
}

/**
 * Dismiss an alert (saves to localStorage).
 */
export function dismissAlert(dismissKey) {
  try { localStorage.setItem(`alert_dismissed_${dismissKey}`, Date.now().toString()); }
  catch { /* intentionally empty */ }
}
